import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, FileText, Loader2, Paperclip, ShieldCheck, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogBody as DialogBody,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
} from "@/components/ui/responsive-dialog";
import { DatePicker } from "@/components/DatePicker";
import { formatDate, todayISO } from "@/lib/format";
import { ACCEPT_DOCS, isPdf, formatBytes, uploadLooseDocument } from "@/lib/documents";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const RESULTS = [
  { value: "godkjent", label: "Godkjent", tone: "ok" },
  { value: "godkjent_med_merknad", label: "Godkjent m/ merknad", tone: "warn" },
  { value: "ikkje_godkjent", label: "Ikkje godkjent", tone: "bad" },
];

const addMonths = (iso, months) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() !== day) d.setDate(0);
  return d.toISOString().slice(0, 10);
};

/**
 * RegisterControlDialog – «Årskontroll utført» registrert separat frå servicerapport.
 * machine: {id, label, control_interval_months, status.next_control_date} (from detail or calendar event)
 * POST /machines/{id}/control → onDone(updatedMachine)
 */
export const RegisterControlDialog = ({ open, onOpenChange, machine, onDone }) => {
  const inputRef = useRef(null);
  const [date, setDate] = useState(todayISO());
  const [nextDate, setNextDate] = useState("");
  const [result, setResult] = useState("godkjent");
  const [note, setNote] = useState("");
  const [docs, setDocs] = useState([]); // {id, title, size, uploading}
  const [busy, setBusy] = useState(false);

  const interval = machine?.control_interval_months || 12;
  const autoNext = useMemo(() => (date ? addMonths(date, interval) : ""), [date, interval]);

  useEffect(() => {
    if (!open) return;
    setDate(todayISO());
    setNextDate("");
    setResult("godkjent");
    setNote("");
    setDocs([]);
  }, [open, machine?.id]);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      if (!isPdf(f) && !f.type.startsWith("image/")) { toast.error(`${f.name}: berre PDF eller bilete.`); continue; }
      const tmp = { id: `tmp-${Date.now()}-${Math.random()}`, title: f.name, size: f.size, uploading: true };
      setDocs((d) => [...d, tmp]);
      try {
        const rec = await uploadLooseDocument(f, { title: f.name.replace(/\.[^.]+$/, ""), category: "aarskontroll", docDate: date });
        setDocs((d) => d.map((x) => (x.id === tmp.id ? { id: rec.id, title: rec.title || f.name, size: rec.size, pdf: isPdf(rec) } : x)));
      } catch (err) {
        setDocs((d) => d.filter((x) => x.id !== tmp.id));
        toast.error(err?.response?.data?.detail || `Kunne ikkje laste opp ${f.name}.`);
      }
    }
  };
  const removeDoc = async (d) => {
    setDocs((list) => list.filter((x) => x.id !== d.id));
    if (!d.uploading) api.delete(`/files/${d.id}`).catch(() => {});
  };

  const uploading = docs.some((d) => d.uploading);

  const submit = async () => {
    if (!machine?.id) return;
    if (!date) return toast.error("Vel dato for utført kontroll.");
    if (uploading) return toast.info("Vent til dokumenta er ferdig lasta opp.");
    setBusy(true);
    try {
      const r = await api.post(`/machines/${machine.id}/control`, {
        date,
        next_control_date: nextDate || null,
        result,
        note: note.trim() || null,
        document_ids: docs.filter((d) => !d.uploading).map((d) => d.id),
      });
      toast.success(`Årskontroll registrert. Neste frist ${formatDate(r.data.next_control_date)}.`);
      onDone?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje registrere årskontroll.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)} snapPoints={[0.9]}>
      <DialogContent className="sm:max-w-lg" data-testid="register-control-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><ShieldCheck className="h-5 w-5 text-primary" /> Registrer årskontroll</DialogTitle>
          <DialogDescription>
            {machine?.label || "Maskin"}{machine?.status?.next_control_date ? ` · gjeldande frist ${formatDate(machine.status.next_control_date)}` : ""}. Uavhengig av service – set ny frist og legg ved kontrollrapporten.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Utført dato *</Label>
              <DatePicker value={date} onChange={setDate} clearable={false} testid="control-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Neste kontroll <span className="font-normal text-muted-foreground">– valfritt</span></Label>
              <DatePicker value={nextDate} onChange={setNextDate} placeholder={autoNext ? `Auto: ${formatDate(autoNext)}` : "Vel dato"} testid="control-next-date" />
            </div>
          </div>
          <p className="-mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> Utan eigen dato blir neste frist sett {interval} månader fram{autoNext ? ` (${formatDate(autoNext)})` : ""}.</p>

          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Resultat</Label>
            <div className="grid grid-cols-3 gap-2" data-testid="control-result">
              {RESULTS.map((r) => (
                <button key={r.value} type="button" onClick={() => setResult(r.value)} aria-pressed={result === r.value} data-testid={`control-result-${r.value}`}
                  className={cn("pressable h-11 rounded-lg border px-2 text-xs font-semibold transition-colors",
                    result === r.value
                      ? r.tone === "ok" ? "border-transparent bg-[#1F8A4C] text-white" : r.tone === "warn" ? "border-transparent bg-primary text-[#0B0D10]" : "border-transparent bg-[rgb(var(--status-overdue))] text-white"
                      : "bg-card hover:bg-accent")}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Notat <span className="font-normal text-muted-foreground">– kontrollorgan, merknader</span></Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="t.d. Utført av Kiwa. Merknad: slitasje på wire." data-testid="control-note" />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Kontrollrapport / sertifikat <span className="font-normal text-muted-foreground">– PDF eller bilete</span></Label>
              <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} data-testid="control-add-doc"><Paperclip /> Legg ved</Button>
              <input ref={inputRef} type="file" accept={ACCEPT_DOCS} multiple className="hidden" onChange={pick} data-testid="control-doc-input" />
            </div>
            {docs.length === 0 ? (
              <button type="button" onClick={() => inputRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-4 text-xs text-muted-foreground hover:bg-accent" data-testid="control-doc-dropzone">
                <Upload className="h-4 w-4" /> Legg ved rapporten frå sertifiseringsorganet – ho blir lagra under «Dokument» på maskina.
              </button>
            ) : (
              <ul className="divide-y divide-border rounded-lg border" data-testid="control-doc-list">
                {docs.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    {d.uploading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" /> : <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <span className="min-w-0 flex-1 truncate">{d.title}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(d.size)}</span>
                    <Button type="button" size="iconSm" variant="ghost" onClick={() => removeDoc(d)} aria-label="Fjern"><X className="h-4 w-4" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="control-cancel">Avbryt</Button>
          <Button onClick={submit} disabled={busy || uploading} data-testid="control-save">{busy ? <Loader2 className="animate-spin" /> : <ShieldCheck />} Registrer årskontroll</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
